import React, { useState } from 'react'
import type { CalendarEvent } from 'paintscout'
import moment from 'moment'
import { makeStyles, Avatar, Typography } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { getCrews } from '@paintscout/util/builder'
import { useProductionCalendar } from '../ProductionCalendarContext'
import ProductionEventPopper from '../ProductionEventPopper'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between'
  },

  event: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    display: 'flex',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing(2),
    alignItems: 'center',
    boxShadow: theme.shadows[0],
    transition: 'box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        boxShadow: theme.shadows[1],
        transition: 'box-shadow 0.2s ease-in-out'
      }
    }
  },
  avatar: {
    padding: '0px !important'
  },
  noEvents: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5, 6, 3, 6),
    gap: theme.spacing(2),
    color: theme.paintscout.palette.brand.Neutral.P90
  }
}))

interface MobileEventTableProps {
  events: CalendarEvent[]
}

export default function MobileEventTable({ events }: MobileEventTableProps) {
  const classes = useStyles()
  const { date } = useProductionCalendar()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { options } = useClientOptions()

  const crews = getCrews({ options })

  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  const getEventCrew = (event: CalendarEvent) => {
    return event.crew ? crews.find((crew) => crew._id === event.crew) : null
  }

  const handleOpenDrawer = (e: React.MouseEvent<HTMLDivElement>, event: CalendarEvent) => {
    if ((e.target as HTMLElement).classList?.contains('disable-click')) {
      return
    }
    setAnchorEl(e.currentTarget)
    setSelectedEvent(event)
  }

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography weight={700}>{moment(date).format('dddd, MMMM D, YYYY')}</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {orderedEvents.length} {orderedEvents.length === 1 ? 'job' : 'jobs'}
          </Typography>
        </div>
        {orderedEvents.length ? (
          <div>
            {orderedEvents.map((event) => {
              return (
                <div key={event.id} className={classes.event} onClick={(e) => handleOpenDrawer(e, event)}>
                  <Avatar
                    className={classes.avatar}
                    avatarColor={getEventCrew(event)?.color}
                    filled
                    width="24px"
                    height="24px"
                    fontSize={'10px'}
                  >
                    {getEventCrew(event)?.name}
                  </Avatar>
                  <Typography>{event?.deal?.name}</Typography>
                </div>
              )
            })}
          </div>
        ) : (
          <div className={classes.noEvents}>
            <i className="fa-regular fa-calendar fa-2x" />
            <Typography variant="subtitle1" color="textSecondary">
              No jobs scheduled
            </Typography>
          </div>
        )}
      </div>
      <ProductionEventPopper event={selectedEvent} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} closeOnEdit />
    </>
  )
}
